import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  MdAdd,
  MdAutoAwesome,
  MdAccountTree,
  MdChevronLeft,
  MdChevronRight,
  MdToday,
  MdViewKanban,
  MdViewTimeline,
  MdChecklist,
  MdViewColumn,
} from "react-icons/md";
import Card from "../../../components/card";
import api from "../../../services/axios";
import {
  autoSchedule,
  getBoardColumns,
  getSchedules,
  getTasks,
  getWorkedByTask,
  updateTask,
  updateTaskSchedule,
  updateTaskStatus,
} from "../../../services/projectTaskApi";
import { BoardColumn, ProjectTask, TaskStatus, TaskType } from "../../../interfaces/ProjectTask";
import TaskBoard from "./components/TaskBoard";
import TaskGantt, { GANTT_GROUP_LABELS, GanttGroupBy } from "./components/TaskGantt";
import {
  filterGanttTasks,
  GANTT_PERIOD_LABELS,
  GANTT_STATUS_LABELS,
  GanttPeriod,
  GanttStatus,
} from "./ganttFilters";
import GanttPdfButton from "./components/GanttPdfButton";
import TaskHierarchy from "./components/TaskHierarchy";
import TaskModal from "./components/TaskModal";
import BoardColumnsModal from "./components/BoardColumnsModal";
import SearchableSelect from "../../../components/searchableSelect/SearchableSelect";
import MultiSelect from "../../../components/searchableSelect/MultiSelect";
import { toast } from "react-toastify";
import {
  BoardPeriod,
  addDays,
  filterTasksByPeriod,
  keepWithAncestors,
  parseIsoDate,
  periodRange,
  toDateInput,
} from "./boardPeriod";

interface ProjectOption {
  id: number;
  projectName: string;
  clientId?: number | null;
  clientName?: string | null;
  status?: number;
}

// Projeto ativo = ProjectStatus.Active (1). Filtros só mostram ativos.
const PROJECT_ACTIVE = 1;

interface EmployeeOption {
  id: number;
  name: string;
}

const ALL = "all";
type Tab = "hierarchy" | "board" | "gantt";

const ganttSelectCls =
  "h-9 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";

const BOARD_PERIOD_LABEL: Record<BoardPeriod, string> = { all: "Tudo", week: "Semana", month: "Mês" };
const fmtShort = (d: Date) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

const ProjectTasksPage: React.FC = () => {
  const location = useLocation();

  /**
   * Tarefa a abrir assim que ela existir na lista — é como a tela de Chamados manda a pessoa
   * direto para a tarefa do chamado em vez de largá-la no meio de todas as tarefas da empresa.
   *
   * Lido no inicializador, e não dentro de um efeito: num efeito, a tarefa reabriria sozinha a
   * cada recarga silenciosa da lista.
   *
   * ⚠ Por montagem, não por sessão: `location.state` vive no histórico do browser, então um F5
   * nesta página reabre a mesma tarefa. É a tarefa que a pessoa pediu, então o comportamento é
   * inofensivo — mas não é "uma vez só", e ninguém deve depender disso.
   */
  const [pendingTaskId, setPendingTaskId] = useState<number | null>(
    () => (location.state as { openTaskId?: number } | null)?.openTaskId ?? null
  );

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [empCapacity, setEmpCapacity] = useState<Record<number, number>>({});
  const [clientFilter, setClientFilter] = useState<string>(ALL);
  /**
   * Nasce no projeto de quem chamou, quando veio de um deep-link. Sem isto a lista viria com o
   * filtro em "todos" e a tarefa poderia simplesmente não estar carregada — o `find` falharia em
   * silêncio e o link pareceria não fazer nada.
   */
  const [projectFilter, setProjectFilter] = useState<string>(() => {
    const projectId = (location.state as { projectId?: number } | null)?.projectId;
    return projectId ? String(projectId) : ALL;
  });
  const [employeeFilter, setEmployeeFilter] = useState<(string | number)[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [schedules, setSchedules] = useState<Record<number, { date: string; plannedHours: number }[]>>({});
  const [workedByTask, setWorkedByTask] = useState<Record<number, number>>({});
  const [tab, setTab] = useState<Tab>("board");
  // Filtro temporal da aba Board (padrão do Capacity): tudo / semana / mês + data-âncora.
  // A âncora fica na data escolhida (sem snap p/ segunda): periodRange normaliza a semana,
  // e no modo mês a segunda ISO poderia cair no mês anterior e mostrar o mês errado.
  const [boardPeriod, setBoardPeriod] = useState<BoardPeriod>("all");
  const [boardAnchor, setBoardAnchor] = useState<Date>(() => new Date());
  // Filtros PRÓPRIOS da aba Cronograma. Separados dos do Board porque respondem a outra pergunta:
  // o Board mostra o que está na mesa agora, o cronograma mostra a linha do tempo — e é nele que
  // tarefa antiga e já concluída polui a leitura.
  const [ganttPeriod, setGanttPeriod] = useState<GanttPeriod>("all");
  const [ganttStatus, setGanttStatus] = useState<GanttStatus>("all");
  const [ganttGroupBy, setGanttGroupBy] = useState<GanttGroupBy>("epic");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectTask | null>(null);
  const [scheduling, setScheduling] = useState(false);
  // Contexto de criação (vindo da árvore: tipo/pai/projeto pré-definidos).
  const [addType, setAddType] = useState<number>(TaskType.Task);
  const [addParentId, setAddParentId] = useState<number | null>(null);
  const [addProjectId, setAddProjectId] = useState<number | null>(null);

  useEffect(() => {
    api
      .get("/Project/Projects")
      .then((r) => {
        setProjects(
          r.data.map((p: any) => ({
            id: p.id,
            projectName: p.projectName,
            clientId: p.clientId,
            clientName: p.clientName,
            status: p.status,
          }))
        );
      })
      .catch((e) => console.error("Erro ao carregar projetos", e));

    api
      .get("/Employee/employees")
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).filter((e: any) => e.flActive);
        setEmployees(list.map((e: any) => ({ id: e.id, name: e.nmEmployee ?? e.email })));
        const cap: Record<number, number> = {};
        list.forEach((e: any) => (cap[e.id] = e.weeklyCapacityHours ?? 40));
        setEmpCapacity(cap);
      })
      .catch((e) => console.error("Erro ao carregar colaboradores", e));

    loadColumns();
  }, []);

  const loadColumns = () => {
    getBoardColumns()
      .then(setColumns)
      .catch((e) => console.error("Erro ao carregar colunas do board", e));
  };

  // Apenas projetos ativos alimentam os filtros.
  const activeProjects = useMemo(
    () => projects.filter((p) => p.status == null || p.status === PROJECT_ACTIVE),
    [projects]
  );

  // Clientes distintos derivados dos projetos ativos.
  const clients = useMemo(() => {
    const map = new Map<number, string>();
    activeProjects.forEach((p) => {
      if (p.clientId != null) map.set(p.clientId, p.clientName ?? `Cliente #${p.clientId}`);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [activeProjects]);

  // Projetos visíveis no filtro de projeto (ativos, limitados pelo cliente selecionado).
  const projectsForFilter = useMemo(() => {
    if (clientFilter === ALL) return activeProjects;
    return activeProjects.filter((p) => String(p.clientId) === clientFilter);
  }, [activeProjects, clientFilter]);

  // Cronogramas (para o Gantt) conforme os filtros.
  const loadSchedules = () => {
    const clientId = clientFilter === ALL ? undefined : Number(clientFilter);
    const projectId = projectFilter === ALL ? undefined : Number(projectFilter);
    getSchedules(clientId, projectId)
      .then((rows) => {
        const map: Record<number, { date: string; plannedHours: number }[]> = {};
        rows.forEach((r) => {
          map[r.taskId] = (r.lines ?? []).map((l) => ({
            date: String(l.date).slice(0, 10),
            plannedHours: l.plannedHours,
          }));
        });
        setSchedules(map);
      })
      .catch((e) => console.error("Erro ao carregar cronogramas", e));
  };

  // Horas realizadas (timesheet) por tarefa — para estimado × realizado.
  const loadWorked = () => {
    const clientId = clientFilter === ALL ? undefined : Number(clientFilter);
    const projectId = projectFilter === ALL ? undefined : Number(projectFilter);
    getWorkedByTask(clientId, projectId)
      .then((rows) => {
        const map: Record<number, number> = {};
        rows.forEach((r) => (map[r.taskId] = r.workedHours));
        setWorkedByTask(map);
      })
      .catch((e) => console.error("Erro ao carregar horas realizadas", e));
  };

  // Recarrega SÓ as tarefas, sem o spinner da tela — sincroniza o que o backend derivou
  // após uma ação otimista (cascata de conclusão, contadores de subtarefas, scheduleState).
  const refreshTasksSilent = () => {
    const clientId = clientFilter === ALL ? undefined : Number(clientFilter);
    const projectId = projectFilter === ALL ? undefined : Number(projectFilter);
    getTasks(clientId, projectId)
      .then(setTasks)
      .catch((e) => console.error("Erro ao recarregar tarefas", e));
  };

  // Recarrega tarefas + cronogramas + realizado conforme os filtros. `silent` pula o spinner
  // e mantém a visão montada, preservando estados locais (ex.: nós recolhidos da hierarquia).
  const loadAll = (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) setLoading(true);
    const clientId = clientFilter === ALL ? undefined : Number(clientFilter);
    const projectId = projectFilter === ALL ? undefined : Number(projectFilter);
    getTasks(clientId, projectId)
      .then(setTasks)
      .catch((e) => console.error("Erro ao carregar tarefas", e))
      .finally(() => {
        if (!silent) setLoading(false);
      });
    loadSchedules();
    loadWorked();
  };

  // Com spinner: só load inicial e troca de filtros. Sem: interações (salvar, colunas, auto-schedule).
  const loadTasks = () => loadAll();
  const refreshAllSilent = () => loadAll({ silent: true });

  // Persiste a edição do cronograma do Gantt de forma OTIMISTA (sem recarregar a tela toda).
  // O TaskGantt já atualizou o próprio draft; aqui só sincronizamos o estado e gravamos em 2º plano.
  const handleScheduleChange = (
    taskId: number,
    lines: { date: string; plannedHours: number }[]
  ) => {
    setSchedules((prev) => ({ ...prev, [taskId]: lines })); // otimista (mantém o pai em sincronia)
    updateTaskSchedule(taskId, lines).catch((e) => {
      console.error("Erro ao salvar cronograma", e);
      loadSchedules(); // rollback: recarrega só os cronogramas (sem spinner da tela)
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadTasks, [clientFilter, projectFilter]);

  /**
   * Abre a tarefa pedida por um deep-link assim que ela chega na lista.
   *
   * Roda depois do carregamento porque o `state` da navegação traz o ID, não a tarefa — e o modal
   * de edição precisa do objeto inteiro. O `pendingTaskId` é zerado no primeiro acerto para a
   * tarefa não reabrir a cada recarga silenciosa da lista.
   *
   * ⚠ Não encontrando, desiste em silêncio em vez de ficar tentando: a tarefa pode ter sido
   * apagada ou estar num projeto inativo, e um modal que nunca abre é melhor que um efeito que
   * roda para sempre.
   */
  useEffect(() => {
    if (pendingTaskId === null) return;
    if (tasks.length === 0) return;

    const alvo = tasks.find((t) => t.id === pendingTaskId);
    setPendingTaskId(null);
    if (!alvo) return;

    setEditing(alvo);
    setModalOpen(true);
  }, [tasks, pendingTaskId]);

  const onClientChange = (value: string) => {
    setClientFilter(value);
    setProjectFilter(ALL); // reseta o projeto ao trocar de cliente
  };

  // Filtro de colaborador (multi-seleção). Mantém os ancestrais (épico/tarefa-pai) p/ a árvore/agrupamento.
  const selectedEmpIds = useMemo(() => employeeFilter.map(Number), [employeeFilter]);
  const matchedTasks = useMemo(() => {
    if (selectedEmpIds.length === 0) return tasks;
    return tasks.filter((t) => {
      const ids = new Set<number>();
      if (t.assigneeEmployeeId != null) ids.add(t.assigneeEmployeeId);
      t.assignees?.forEach((a) => ids.add(a.id));
      return selectedEmpIds.some((id) => ids.has(id));
    });
  }, [tasks, selectedEmpIds]);

  const viewTasks = useMemo(() => {
    if (selectedEmpIds.length === 0) return tasks;
    const matchedIds = new Set(matchedTasks.map((t) => t.id));
    return keepWithAncestors(tasks, (t) => matchedIds.has(t.id));
  }, [tasks, matchedTasks, selectedEmpIds]);

  // Aba Board: filtro temporal client-side sobre as tarefas já filtradas por cliente/projeto/colaborador.
  const boardRange = useMemo(() => periodRange(boardPeriod, boardAnchor), [boardPeriod, boardAnchor]);
  const boardTasks = useMemo(() => filterTasksByPeriod(viewTasks, boardRange), [viewTasks, boardRange]);

  // Aba Cronograma: período + situação. Aplicado AQUI, e não dentro do TaskGantt, porque o botão de
  // exportar PDF recebe a mesma lista — com o filtro dentro do componente, filtrar a tela e
  // exportar produziria um PDF com tudo, e a pessoa só descobriria depois de mandar o arquivo.
  const ganttTasks = useMemo(
    () => filterGanttTasks(viewTasks, schedules, ganttPeriod, ganttStatus),
    [viewTasks, schedules, ganttPeriod, ganttStatus]
  );

  // `projectId` → cliente, para o agrupamento por cliente. Sai da lista de projetos que a tela já
  // carrega: `ProjectTaskViewModel` não traz cliente, e acrescentá-lo no backend seria construir um
  // dado que já está na mão.
  const projectClients = useMemo(() => {
    const m: Record<number, { clientId?: number | null; clientName?: string | null }> = {};
    projects.forEach((p) => (m[p.id] = { clientId: p.clientId, clientName: p.clientName }));
    return m;
  }, [projects]);

  // Passo das setas do período do board: semana anda 7 dias; mês anda 1 mês-calendário.
  const shiftBoardPeriod = (n: number) =>
    setBoardAnchor((prev) =>
      boardPeriod === "month"
        ? new Date(prev.getFullYear(), prev.getMonth() + n, 1)
        : addDays(prev, n * 7)
    );

  const pickBoardDate = (value: string) => {
    if (!value) return;
    setBoardAnchor(parseIsoDate(value));
  };

  const boardPeriodLabel = boardRange
    ? boardPeriod === "month"
      ? boardRange.start.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
      : `${fmtShort(boardRange.start)} a ${fmtShort(boardRange.end)}`
    : null;

  const stats = useMemo(() => {
    const base = matchedTasks;
    const total = base.length;
    const done = base.filter((t) => t.statusCategory === "Done").length;
    const overdue = base.filter((t) => t.isOverdue).length;
    const estimated = base.reduce((acc, t) => acc + (t.estimatedHours ?? 0), 0);
    return { total, done, overdue, estimated };
  }, [matchedTasks]);

  const handleMove = async (taskId: number, newStatus: number, newIndex: number) => {
    // Reordena localmente (otimista) recalculando o orderIndex da coluna de destino.
    // Mesma regra do backend: insere na posição pedida (clampada) e reindexa 0..n-1.
    // Atualiza também a categoria/nome/cor da coluna — a Hierarquia risca por statusCategory,
    // então mover para "Concluído" no board já reflete lá sem esperar reload.
    const destColumn = columns.find((c) => c.id === newStatus);
    setTasks((prev) => {
      const moving = prev.find((t) => t.id === taskId);
      if (!moving) return prev;
      const without = prev.filter((t) => t.id !== taskId);
      const dest = without
        .filter((t) => t.status === newStatus)
        .sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id);
      dest.splice(Math.min(Math.max(newIndex, 0), dest.length), 0, {
        ...moving,
        status: newStatus,
        statusCategory: destColumn?.category ?? moving.statusCategory,
        statusName: destColumn?.name ?? moving.statusName,
        statusColor: destColumn?.color ?? moving.statusColor,
      });
      const destReindexed = dest.map((t, i) => ({ ...t, orderIndex: i }));
      const rest = without.filter((t) => t.status !== newStatus);
      return [...rest, ...destReindexed];
    });

    try {
      await updateTaskStatus(taskId, newStatus, Math.max(newIndex, 0));
      // Concluir no board cascateia para as subtarefas no backend — sincroniza sem spinner.
      refreshTasksSilent();
    } catch (e) {
      console.error("Erro ao mover tarefa", e);
      toast.error("Erro ao mover a tarefa — a ordem foi restaurada.");
      refreshTasksSilent(); // rollback do otimista sem desmontar a visão
    }
  };

  const selectedProjectId = projectFilter === ALL ? null : Number(projectFilter);
  const isMultiProject = selectedProjectId === null;

  const handleAutoSchedule = async () => {
    setScheduling(true);
    try {
      const res = await autoSchedule(selectedProjectId ?? undefined);
      refreshAllSilent();
      window.alert(
        `Cronograma gerado: ${res.scheduled} tarefa(s) agendada(s)` +
          (res.skippedLocked ? `, ${res.skippedLocked} com datas fixadas mantida(s)` : "") +
          ".\nDefina estimativa e responsável nas tarefas para que entrem no cronograma."
      );
    } catch (e) {
      console.error("Erro ao gerar cronograma", e);
    } finally {
      setScheduling(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setAddType(TaskType.Task);
    setAddParentId(null);
    setAddProjectId(selectedProjectId);
    setModalOpen(true);
  };

  const openEdit = (task: ProjectTask) => {
    setEditing(task);
    setModalOpen(true);
  };

  /**
   * Re-vincula a tarefa arrastada na árvore.
   *
   * ⚠ `PUT /ProjectTask` é **replace**: campo ausente é campo apagado (título, descrição,
   * estimativa, responsáveis, dependências, ScheduleLocked). Por isso o payload reenvia a tarefa
   * inteira e troca só o pai — mandar `{ id, parentTaskId }` limparia o resto em silêncio.
   *
   * O `type` não vai: o servidor o deriva do nível do destino, e é ele a autoridade.
   */
  const handleReparent = async (task: ProjectTask, newParentId: number | null) => {
    try {
      await updateTask({
        id: task.id,
        projectId: task.projectId,
        title: task.title,
        description: task.description ?? undefined,
        estimatedHours: task.estimatedHours ?? null,
        assigneeEmployeeIds: (task.assignees ?? []).map((a) => a.id),
        status: task.status,
        priority: task.priority,
        dependsOnTaskIds: task.dependsOnTaskIds ?? [],
        scheduleLocked: task.scheduleLocked ?? false,
        parentTaskId: newParentId,
      });
      await refreshAllSilent();
    } catch (e: any) {
      // A mensagem do servidor diz o que fazer (ciclo, por exemplo) — engoli-la deixaria o
      // arrasto "sem efeito" e sem pista, que é o estado que motivou esta change.
      console.error("Erro ao re-vincular tarefa", e);
      toast.error(e?.response?.data?.message ?? "Não foi possível mover a tarefa.");
    }
  };

  // Criação a partir da árvore: tipo + pai pré-definidos; projeto herdado do pai (ou do filtro).
  const handleAddFromTree = (type: number, parentId: number | null) => {
    const parentProjectId =
      parentId != null ? tasks.find((t) => t.id === parentId)?.projectId ?? null : null;
    setEditing(null);
    setAddType(type);
    setAddParentId(parentId);
    setAddProjectId(parentProjectId ?? selectedProjectId);
    setModalOpen(true);
  };

  // Checkbox de conclusão (concluir o pai conclui os filhos — cascata no backend).
  // As colunas do board são dinâmicas: concluir vai para a 1ª coluna de categoria "Done";
  // reabrir volta para "A Fazer" (se existir) ou para a 1ª coluna "NotStarted".
  const handleToggleComplete = async (task: ProjectTask) => {
    const completing = task.statusCategory !== "Done";
    const byPosition = (a: BoardColumn, b: BoardColumn) => a.position - b.position;
    const target = completing
      ? columns.filter((c) => c.category === "Done").sort(byPosition)[0]
      : columns.find((c) => c.id === TaskStatus.Todo && c.category === "NotStarted") ??
        columns.filter((c) => c.category === "NotStarted").sort(byPosition)[0];
    const next = target?.id ?? (completing ? TaskStatus.Done : TaskStatus.Todo);

    // Otimista: atualiza status + categoria (o risco da hierarquia usa statusCategory) e,
    // ao concluir, espelha a cascata do backend nos descendentes.
    setTasks((prev) => {
      const affected = new Set<number>([task.id]);
      if (completing) {
        const childrenOf = new Map<number, number[]>();
        prev.forEach((t) => {
          if (t.parentTaskId != null) {
            const arr = childrenOf.get(t.parentTaskId) ?? [];
            arr.push(t.id);
            childrenOf.set(t.parentTaskId, arr);
          }
        });
        const walk = (id: number) =>
          (childrenOf.get(id) ?? []).forEach((k) => {
            affected.add(k);
            walk(k);
          });
        walk(task.id);
      }
      return prev.map((t) =>
        affected.has(t.id)
          ? {
              ...t,
              status: next,
              statusCategory: target?.category ?? (completing ? "Done" : "NotStarted"),
              statusName: target?.name ?? t.statusName,
              statusColor: target?.color ?? t.statusColor,
            }
          : t
      );
    });

    try {
      await updateTaskStatus(task.id, next);
      refreshTasksSilent();
    } catch (e) {
      console.error("Erro ao atualizar conclusão", e);
      toast.error("Erro ao atualizar a conclusão — o estado foi restaurado.");
      refreshTasksSilent(); // rollback do otimista sem desmontar a visão
    }
  };

  return (
    <div className="mt-5 flex flex-col gap-4 animate-fade-up">
      <Card extra="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <span
            className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
            aria-hidden="true"
          >
            <MdChecklist className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Gestão de Projetos
            </span>
            <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
              Tarefas
            </h1>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-3xl">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="task-client" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Cliente
              </label>
              <SearchableSelect
                placeholder="Todos os clientes"
                value={clientFilter === ALL ? null : Number(clientFilter)}
                onChange={(v) => onClientChange(v == null ? ALL : String(v))}
                options={clients.map((c) => ({ label: c.name, value: c.id }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="task-project" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Projeto
              </label>
              <SearchableSelect
                placeholder="Todos os projetos"
                value={projectFilter === ALL ? null : Number(projectFilter)}
                onChange={(v) => setProjectFilter(v == null ? ALL : String(v))}
                options={projectsForFilter.map((p) => ({ label: p.projectName, value: p.id }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="task-employee" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Colaborador
              </label>
              <MultiSelect
                placeholder="Todos os colaboradores"
                value={employeeFilter}
                onChange={setEmployeeFilter}
                options={employees.map((e) => ({ label: e.name, value: e.id }))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2.5 sm:flex-row">
            <button
              type="button"
              onClick={() => setColumnsModalOpen(true)}
              title="Criar, editar e reordenar as etapas/colunas do board (ex.: Blocked)"
              className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              <MdViewColumn className="h-4 w-4" aria-hidden="true" />
              Colunas
            </button>
            <button
              type="button"
              onClick={handleAutoSchedule}
              disabled={scheduling}
              title="Gera as datas das tarefas a partir das estimativas e do capacity diário do responsável"
              className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              <MdAutoAwesome className="h-4 w-4" aria-hidden="true" />
              {scheduling ? "Gerando…" : "Gerar cronograma"}
            </button>
            <button
              type="button"
              onClick={openNew}
              disabled={selectedProjectId === null}
              title={selectedProjectId === null ? "Selecione um projeto específico para criar uma tarefa" : ""}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
            >
              <MdAdd className="h-4 w-4" aria-hidden="true" />
              Nova tarefa
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Kpi label="Tarefas" value={stats.total} />
          <Kpi label="Concluídas" value={stats.done} tone="green" />
          <Kpi label="Atrasadas" value={stats.overdue} tone="red" />
          <Kpi label="Horas estimadas" value={`${stats.estimated}h`} />
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <TabButton active={tab === "hierarchy"} onClick={() => setTab("hierarchy")} icon={<MdAccountTree className="h-[18px] w-[18px]" aria-hidden="true" />}>
          Hierarquia
        </TabButton>
        <TabButton active={tab === "board"} onClick={() => setTab("board")} icon={<MdViewKanban className="h-[18px] w-[18px]" aria-hidden="true" />}>
          Board
        </TabButton>
        <TabButton active={tab === "gantt"} onClick={() => setTab("gantt")} icon={<MdViewTimeline className="h-[18px] w-[18px]" aria-hidden="true" />}>
          Cronograma (Gantt)
        </TabButton>
      </div>

      <Card extra="p-4 sm:p-5">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
        ) : tab === "hierarchy" ? (
          <TaskHierarchy
            tasks={viewTasks}
            onEdit={openEdit}
            onAdd={handleAddFromTree}
            onToggleComplete={handleToggleComplete}
            canAdd={selectedProjectId !== null}
            workedByTask={workedByTask}
            onReparent={handleReparent}
          />
        ) : tab === "board" ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-xl border border-gray-200 p-0.5 dark:border-white/10">
                {(["all", "week", "month"] as BoardPeriod[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setBoardPeriod(p)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                      boardPeriod === p
                        ? "bg-dv-gradient text-white shadow-dv-sm"
                        : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-navy-700"
                    }`}
                  >
                    {BOARD_PERIOD_LABEL[p]}
                  </button>
                ))}
              </div>
              {boardRange && (
                <>
                  <button
                    type="button"
                    aria-label="Período anterior"
                    title="Período anterior"
                    onClick={() => shiftBoardPeriod(-1)}
                    className="rounded-xl border border-gray-200 p-2 text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                  >
                    <MdChevronLeft size={18} aria-hidden="true" />
                  </button>
                  <input
                    type="date"
                    value={toDateInput(boardAnchor)}
                    onChange={(e) => pickBoardDate(e.target.value)}
                    title="Ir para o período de uma data"
                    aria-label="Ir para o período de uma data"
                    className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setBoardAnchor(new Date())}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                  >
                    <MdToday size={16} aria-hidden="true" /> Hoje
                  </button>
                  <button
                    type="button"
                    aria-label="Próximo período"
                    title="Próximo período"
                    onClick={() => shiftBoardPeriod(1)}
                    className="rounded-xl border border-gray-200 p-2 text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                  >
                    <MdChevronRight size={18} aria-hidden="true" />
                  </button>
                  <span className="text-sm font-semibold capitalize text-gray-700 dark:text-gray-300">
                    {boardPeriodLabel}
                  </span>
                </>
              )}
            </div>
            <TaskBoard tasks={boardTasks} columns={columns} onEdit={openEdit} onMove={handleMove} showProject={isMultiProject} />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="gantt-group" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Agrupar por
                  </label>
                  <select
                    id="gantt-group"
                    className={ganttSelectCls}
                    value={ganttGroupBy}
                    onChange={(e) => setGanttGroupBy(e.target.value as GanttGroupBy)}
                  >
                    {Object.entries(GANTT_GROUP_LABELS).map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="gantt-period" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Período
                  </label>
                  <select
                    id="gantt-period"
                    className={ganttSelectCls}
                    value={ganttPeriod}
                    onChange={(e) => setGanttPeriod(e.target.value as GanttPeriod)}
                  >
                    {Object.entries(GANTT_PERIOD_LABELS).map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="gantt-status" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Situação
                  </label>
                  <select
                    id="gantt-status"
                    className={ganttSelectCls}
                    value={ganttStatus}
                    onChange={(e) => setGanttStatus(e.target.value as GanttStatus)}
                  >
                    {Object.entries(GANTT_STATUS_LABELS).map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantas linhas o filtro tirou. Sem isto, "sumiu uma tarefa" e "não existe tarefa"
                    ficam indistinguíveis — e o filtro vira suspeito de bug. */}
                {ganttTasks.length !== viewTasks.length && (
                  <span className="pb-2.5 text-[11px] text-gray-500 dark:text-gray-400">
                    {viewTasks.length - ganttTasks.length} fora do filtro
                  </span>
                )}
              </div>

              <GanttPdfButton
                tasks={ganttTasks}
                schedules={schedules}
                employeesCapacity={empCapacity}
                showProject={isMultiProject}
                title="Cronograma — Tarefas"
                fileName="cronograma.pdf"
              />
            </div>
            <TaskGantt
              tasks={ganttTasks}
              onEdit={openEdit}
              showProject={isMultiProject}
              editable
              schedules={schedules}
              employeesCapacity={empCapacity}
              onScheduleChange={handleScheduleChange}
              groupBy={ganttGroupBy}
              projectClients={projectClients}
            />
          </div>
        )}
      </Card>

      {(() => {
        // Ao editar, usa o projeto da tarefa; ao criar, o contexto de criação (árvore) ou o filtro.
        const modalProjectId = editing ? editing.projectId : addProjectId;
        return (
          modalProjectId != null && (
            <TaskModal
              open={modalOpen}
              projectId={modalProjectId}
              employees={employees}
              tasks={tasks}
              columns={columns}
              editing={editing}
              defaultType={addType}
              defaultParentId={addParentId}
              onClose={() => setModalOpen(false)}
              onSaved={refreshAllSilent}
            />
          )
        );
      })()}

      <BoardColumnsModal
        open={columnsModalOpen}
        columns={columns}
        onClose={() => setColumnsModalOpen(false)}
        onChanged={() => {
          loadColumns();
          refreshAllSilent();
        }}
      />
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: React.ReactNode; tone?: "green" | "red" }> = ({
  label,
  value,
  tone,
}) => (
  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5 dark:border-white/10 dark:bg-navy-900">
    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</p>
    <p
      className={`mt-0.5 font-display text-xl font-bold tabular-nums ${
        tone === "green"
          ? "text-emerald-600 dark:text-emerald-400"
          : tone === "red"
          ? "text-red-600 dark:text-red-400"
          : "text-navy-700 dark:text-white"
      }`}
    >
      {value}
    </p>
  </div>
);

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all active:scale-[.98] ${
      active
        ? "bg-ink-900 text-white shadow-dv-md dark:bg-white dark:text-ink-900"
        : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700"
    }`}
  >
    {icon}
    {children}
  </button>
);

export default ProjectTasksPage;
